import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Plus, Smile, Gift, Send } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { demoMessages, demoResponses, initialResponses, randomInitialResponses, responseTemplates } from './content/DiscordMessages';

interface Message {
  id: number;
  sender: 'user' | 'agent';
  text: string;
  timestamp: Date;
  isLoading?: boolean;
  toolCalls?: ToolCall[];
  error?: string;
}

interface ToolCall {
  tool: string;
  input?: any;
  output?: any;
  status: 'running' | 'complete' | 'error';
  errorDetails?: string;
}

interface DiscordInterfaceProps {
  initialMessages?: Message[];
  onSendMessage: (message: string) => void;
  autoDemo?: boolean;
}

const DiscordInterface: React.FC<DiscordInterfaceProps> = ({ initialMessages = [], onSendMessage, autoDemo = true }) => {
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [demoComplete, setDemoComplete] = useState(false);
  const [demoInProgress, setDemoInProgress] = useState(false);
  const demoRunningRef = useRef(false);
  const demoLoopCountRef = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesRef = useRef<Message[]>(messages);
  const [textareaHeight, setTextareaHeight] = useState('auto');

  // Keep the messagesRef updated with the latest messages
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Sample servers and channels for Discord sidebar
  const servers = [
    { id: 1, name: 'AgentSpot', active: true, hasNotification: false, image: null },
    { id: 2, name: 'Gaming', active: false, hasNotification: true, image: null },
    { id: 3, name: 'Work Projects', active: false, hasNotification: false, image: null },
    { id: 4, name: 'Study Group', active: false, hasNotification: false, image: null },
  ];

  const channels = {
    text: [
      { id: 1, name: 'general', unread: false, selected: false },
      { id: 2, name: 'bot-commands', unread: true, selected: true },
      { id: 3, name: 'announcements', unread: false, selected: false },
    ],
    voice: [
      { id: 4, name: 'General Voice', users: 3 },
      { id: 5, name: 'Gaming', users: 0 },
      { id: 6, name: 'Music', users: 2 },
    ]
  };

  const members = [
    { id: 1, name: 'AgentBot', status: 'online', isBot: true },
    { id: 2, name: 'SteveR', status: 'online', role: 'Admin' },
    { id: 3, name: 'JaneD', status: 'idle' },
    { id: 4, name: 'AlexK', status: 'dnd' },
    { id: 5, name: 'MikeP', status: 'offline' },
  ];

  // Fisher-Yates shuffle algorithm for randomizing message order
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Auto demo functionality
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (autoDemo && demoMessages.length > 0 && !demoComplete && !demoInProgress && !demoRunningRef.current && (messages.length === 0 || demoLoopCountRef.current > 0)) {
      setDemoInProgress(true);
      demoRunningRef.current = true;
      runDemoSequence();
    }
  }, [autoDemo, messages.length, demoComplete, demoInProgress]);
  
  // Disabled exhaustive-deps for this effect because runDemoSequence would create a circular reference

  // Function to simulate typing and sending a message
  const simulateTyping = (message: string) => {
    return new Promise<void>((resolve) => {
      // Clear any existing text
      setInputValue('');
      
      // Simulate typing character by character
      let currentCharIndex = 0;
      const typingInterval = setInterval(() => {
        if (currentCharIndex <= message.length) {
          setInputValue(message.substring(0, currentCharIndex));
          currentCharIndex++;
          
          // Adjust height as we type
          if (inputRef.current) {
            const currentScrollHeight = inputRef.current.scrollHeight;
            const maxHeight = 200;
            setTextareaHeight(Math.min(currentScrollHeight, maxHeight) + 'px');
          }
        } else {
          clearInterval(typingInterval);
          
          // Small pause after typing before sending
          setTimeout(() => {
            // Call a specific version of send that uses the current inputValue
            handleSendDemoMessage(message);
            resolve();
          }, 500);
        }
      }, 10 + Math.random() * 10); // Fast typing speed for demos
    });
  };

  // Special version of handleSendMessage specifically for the demo
  const handleSendDemoMessage = (demoMessage: string) => {
    if (!demoMessage.trim()) return;
    
    // Create a unique message ID to prevent duplicates
    const messageId = Date.now();
    
    // Create and add the user message
    const userMessage: Message = {
      id: messageId,
      sender: 'user',
      text: demoMessage,
      timestamp: new Date(),
    };
    
    // Only add this message if it doesn't already exist
    setMessages(prev => {
      // Check if this message already exists
      const exists = prev.some(m => m.text === demoMessage && m.sender === 'user');
      if (exists) return prev;
      return [...prev, userMessage];
    });
    
    setInputValue('');
    setTextareaHeight('auto');
    
    // Simulate agent responding with the predetermined response for this demo message
    setTimeout(() => {
      setIsTyping(true);
    }, 300);
    
    // Simulate agent processing time
    const processingTime = 1000 + Math.random() * 1000;
    
    setTimeout(() => {
      setIsTyping(false);
      
      // Use predefined responses for the demo
      const demoResponse = demoResponses[demoMessage];
      if (!demoResponse) return;
      
      // Get initial response for this tool from imported initialResponses
      const initialResponseText = initialResponses[demoResponse.tool] || 
        "I'm processing your request.";
      
      const responseId = Date.now() + 1;
      const botResponse: Message = {
        id: responseId,
        sender: 'agent',
        text: initialResponseText,
        timestamp: new Date(),
        toolCalls: [
          {
            tool: demoResponse.tool,
            input: demoResponse.input,
            status: 'running',
          }
        ]
      };
      
      // Only add this message if the user message exists and this bot response doesn't
      setMessages(prev => {
        // Check if we already have this response 
        const userExists = prev.some(m => m.text === demoMessage && m.sender === 'user');
        const botExists = prev.some(m => 
          m.sender === 'agent' && 
          m.text === initialResponseText &&
          m.toolCalls?.some(tc => tc.tool === demoResponse.tool)
        );
        
        if (!userExists || botExists) return prev;
        return [...prev, botResponse];
      });
      
      scrollToBottom(); // Restore this call
      
      // Tools always run and complete successfully
      const toolCompletionTime = 1500 + Math.random() * 1000;
      
      setTimeout(() => {
        setMessages(prev => {
          const updatedMessages = [...prev];
          // Find the exact message by comparing content rather than just ID
          const lastMessage = updatedMessages.find(m => 
            m.sender === 'agent' && 
            m.text === initialResponseText &&
            m.toolCalls?.some(tc => tc.tool === demoResponse.tool)
          );
          
          if (lastMessage?.toolCalls && lastMessage.toolCalls.length > 0) {
            const toolCall = lastMessage.toolCalls[0];
            toolCall.status = 'complete';
            toolCall.output = demoResponse.output;
            
            // Select the right template based on the tool
            const template = responseTemplates[toolCall.tool] || responseTemplates.set_reminder;
            
            // Store the initial response (first paragraph)
            const initialResponse = lastMessage.text.split('\n\n')[0];
            
            // Construct the new message
            lastMessage.text = initialResponse;
            lastMessage.text += `\n\n${template.header}`;
            lastMessage.text += `\n\n${template.intro}`;
            lastMessage.text += template.content;
            lastMessage.text += template.conclusion;
          }
          
          return updatedMessages.map(m => 
            m.sender === 'agent' && 
            m.text.startsWith(initialResponseText) && 
            m.toolCalls?.some(tc => tc.tool === demoResponse.tool) && 
            lastMessage ? lastMessage : m
          );
        });
        
        scrollToBottom(); // Restore this call
      }, toolCompletionTime);
    }, processingTime);
  };

  // Run the full demo sequence
  const runDemoSequence = async () => {
    // Increment loop counter
    demoLoopCountRef.current++;
    console.log(`Starting Discord demo loop ${demoLoopCountRef.current}/1`);
    
    // Wait for initial render
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Randomize the order of messages for this loop
    const randomizedMessages = shuffleArray(demoMessages);
    
    // Loop through each demo message
    for (let i = 0; i < randomizedMessages.length; i++) {
      // Wait for previous message to be processed
      if (i > 0) {
        // Wait for agent to finish responding to previous message
        await new Promise(resolve => {
          const checkInterval = setInterval(() => {
            // Use messagesRef to get the latest messages
            const currentMessages = messagesRef.current;
            const lastMessage = currentMessages[currentMessages.length - 1];
            
            if (lastMessage && lastMessage.sender === 'agent' && 
                (!lastMessage.toolCalls || 
                 lastMessage.toolCalls.every(tc => tc.status === 'complete'))) {
              clearInterval(checkInterval);
              // Add some delay between conversations
              setTimeout(resolve, 2000);
            }
          }, 500);
        });
      }
      
      // Type and send the next message
      await simulateTyping(randomizedMessages[i]);
    }
    
    // Only run a single loop, then mark as complete
    demoRunningRef.current = false;
    setDemoInProgress(false);
    setDemoComplete(true);
    
    // Switch to the next platform (WhatsApp)
    const switchPlatformEvent = new CustomEvent('switchPlatform', {
      detail: { platform: 'whatsapp' }
    });
    window.dispatchEvent(switchPlatformEvent);
  };

  // Scroll to bottom of messages when new ones are added
  // Restoring the effect but ensuring it only scrolls the inner container
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      // Only scroll the messages container, not the whole page
      const messagesContainer = messagesEndRef.current.parentElement;
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    }
  };

  const handleSendMessage = useCallback(() => {
    if (inputValue.trim() === '') return;

    const userMessage: Message = {
      id: Date.now(),
      sender: 'user',
      text: inputValue,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    onSendMessage(inputValue);
    setInputValue('');
    setTextareaHeight('auto'); // Reset height

    // Simulate slight delay before typing indicator
    setTimeout(() => {
      setIsTyping(true);
    }, 300);

    // Simulate agent processing time (random duration to feel more natural)
    const processingTime = 1000 + Math.random() * 1000;
    
    setTimeout(() => {
      setIsTyping(false);
      
      // Use imported random initial responses
      const initialResponseText = randomInitialResponses[Math.floor(Math.random() * randomInitialResponses.length)];
      
      // Discord-relevant tool types
      const toolTypes = ['search_game_news', 'play_music', 'generate_price_chart', 'create_poll', 'set_reminder', 'retrieve_campaign_status', 'generate_tournament_brackets'];
      const selectedTool = toolTypes[Math.floor(Math.random() * toolTypes.length)];
      
      // Create appropriate mock input and output for the selected tool
      let toolInput: any = {};
      let mockOutput: any = {};
      
      switch (selectedTool) {
        case 'search_game_news':
          const gameTerms = ['Elden Ring', 'Fortnite', 'League of Legends', 'Minecraft', 'Call of Duty'];
          const randomGame = gameTerms[Math.floor(Math.random() * gameTerms.length)];
          toolInput = { query: `${randomGame} news` };
          mockOutput = { 
            results: [
              { title: `New ${randomGame} Update Released`, url: "https://example.com/game-update", source: "GameSpot" }, 
              { title: `${randomGame} Tournament Announced`, url: "https://example.com/tournament", source: "IGN" }
            ] 
          };
          break;
          
        case 'play_music':
          const musicGenres = ['lo-fi beats', 'gaming mix', 'rock classics', 'chill pop', 'EDM party'];
          const randomGenre = musicGenres[Math.floor(Math.random() * musicGenres.length)];
          toolInput = { query: randomGenre, type: "playlist" };
          mockOutput = { 
            status: "now_playing", 
            track: `${randomGenre.charAt(0).toUpperCase() + randomGenre.slice(1)} Radio`, 
            artist: "Various Artists", 
            queue_position: 1, 
            requester: "Username" 
          };
          break;
          
        case 'generate_price_chart':
          const assets = ['NVDA', 'ETH', 'BTC', 'AAPL', 'MSFT'];
          const selectedAssets = assets.slice(0, 2);
          
          toolInput = { 
            assets: selectedAssets, 
            timeframe: "2 years",
            chart_type: "line"
          };
          
          mockOutput = {
            chart_id: `chart_${Math.floor(Math.random() * 100000)}`,
            chart_title: `${selectedAssets.join(' vs ')} Price Comparison (2-Year)`,
            chart_image_url: "https://placehold.co/600x400/2f3136/5865F2?text=Price+Comparison+Chart",
            assets: [
              {
                symbol: selectedAssets[0],
                name: selectedAssets[0] === 'NVDA' ? 'NVIDIA Corporation' : 
                      selectedAssets[0] === 'ETH' ? 'Ethereum' :
                      selectedAssets[0] === 'BTC' ? 'Bitcoin' :
                      selectedAssets[0] === 'AAPL' ? 'Apple Inc.' : 'Microsoft Corporation',
                current_price: selectedAssets[0] === 'NVDA' ? 950.25 :
                               selectedAssets[0] === 'ETH' ? 3215.65 :
                               selectedAssets[0] === 'BTC' ? 68423.12 :
                               selectedAssets[0] === 'AAPL' ? 217.75 : 437.65,
                percent_change_2y: (Math.random() * 300 + 50).toFixed(1)
              },
              {
                symbol: selectedAssets[1],
                name: selectedAssets[1] === 'NVDA' ? 'NVIDIA Corporation' : 
                      selectedAssets[1] === 'ETH' ? 'Ethereum' :
                      selectedAssets[1] === 'BTC' ? 'Bitcoin' :
                      selectedAssets[1] === 'AAPL' ? 'Apple Inc.' : 'Microsoft Corporation',
                current_price: selectedAssets[1] === 'NVDA' ? 950.25 :
                               selectedAssets[1] === 'ETH' ? 3215.65 :
                               selectedAssets[1] === 'BTC' ? 68423.12 :
                               selectedAssets[1] === 'AAPL' ? 217.75 : 437.65,
                percent_change_2y: (Math.random() * 300 + 50).toFixed(1)
              }
            ],
            analysis: `${selectedAssets[0]} has ${Math.random() > 0.5 ? 'outperformed' : 'underperformed'} ${selectedAssets[1]} over the past 2 years. Both assets saw significant volatility during this period.`,
            timestamp: new Date().toISOString()
          };
          break;
          
        case 'create_poll':
          const pollTopics = [
            { question: "What game should we play tonight?", options: ["Apex Legends", "Valorant", "Minecraft", "Among Us"] },
            { question: "Movie night theme?", options: ["Action", "Comedy", "Horror", "Sci-Fi"] },
            { question: "Weekend plans?", options: ["Gaming Marathon", "Movie Night", "Voice Chat Hangout"] }
          ];
          const randomPoll = pollTopics[Math.floor(Math.random() * pollTopics.length)];
          
          toolInput = { 
            question: randomPoll.question, 
            options: randomPoll.options 
          };
          
          mockOutput = { 
            poll_id: `poll_${Math.floor(Math.random() * 100000)}`, 
            question: randomPoll.question, 
            options: randomPoll.options, 
            status: "created" 
          };
          break;
          
        case 'set_reminder':
        default:
          const reminderTexts = ["Check game deals", "Join the tournament", "Stream time", "Start raid", "Game night"];
          const randomReminder = reminderTexts[Math.floor(Math.random() * reminderTexts.length)];
          const timeOffsets = ["1 hour", "2 hours", "30 minutes", "tomorrow", "this weekend"];
          const randomTimeOffset = timeOffsets[Math.floor(Math.random() * timeOffsets.length)];
          
          toolInput = { 
            reminder_text: randomReminder, 
            time_offset: randomTimeOffset, 
            target: Math.random() > 0.5 ? "user" : "channel" 
          };
          
          // Calculate future timestamp based on current time
          const futureDate = new Date();
          if (randomTimeOffset.includes("hour")) {
            futureDate.setHours(futureDate.getHours() + parseInt(randomTimeOffset));
          } else if (randomTimeOffset.includes("minute")) {
            futureDate.setMinutes(futureDate.getMinutes() + parseInt(randomTimeOffset));
          } else if (randomTimeOffset.includes("tomorrow")) {
            futureDate.setDate(futureDate.getDate() + 1);
          } else if (randomTimeOffset.includes("weekend")) {
            futureDate.setDate(futureDate.getDate() + (6 - futureDate.getDay()));
          }
          
          mockOutput = { 
            reminder_id: `rem_${Math.floor(Math.random() * 100000)}`, 
            reminder_text: randomReminder, 
            remind_at: futureDate.toISOString(), 
            status: "set" 
          };
          break;
          
        case 'retrieve_campaign_status':
          toolInput = { campaign_id: 'campaign_123' };
          mockOutput = {
            campaign_name: 'The Forgotten Crypts',
            session_number: 5,
            session_date: '2023-07-15',
            party_location: 'The Cursed Forest',
            party_members: [
              { name: 'John', level: 10, class: 'Warrior', status: 'Healthy' },
              { name: 'Alice', level: 12, class: 'Mage', status: 'Low health' },
              { name: 'Bob', level: 8, class: 'Rogue', status: 'Cursed' },
              { name: 'Eve', level: 11, class: 'Cleric', status: 'Dead' }
            ],
            quest_status: 'In progress: Defeat the Dragon',
            dm_notes: 'Remember to use the magical artifact to open the secret door.'
          };
          break;
          
        case 'generate_tournament_brackets':
          toolInput = { tournament_id: 'tournament_456' };
          mockOutput = {
            tournament_name: 'Weekly Tournament',
            game: 'League of Legends',
            format: '5v5',
            start_time: '2023-07-16T18:00:00',
            participants: [
              { name: 'Team A', seed: 1, status: 'online' },
              { name: 'Team B', seed: 2, status: 'online' },
              { name: 'Team C', seed: 3, status: 'online' },
              { name: 'Team D', seed: 4, status: 'online' },
              { name: 'Team E', seed: 5, status: 'online' },
              { name: 'Team F', seed: 6, status: 'online' },
              { name: 'Team G', seed: 7, status: 'online' },
              { name: 'Team H', seed: 8, status: 'online' }
            ],
            brackets: [
              {
                round: 1,
                matches: [
                  { id: 1, player1: 'Team A', player2: 'Team B' },
                  { id: 2, player1: 'Team C', player2: 'Team D' },
                  { id: 3, player1: 'Team E', player2: 'Team F' },
                  { id: 4, player1: 'Team G', player2: 'Team H' }
                ]
              },
              {
                round: 2,
                matches: [
                  { id: 5, player1: 'Winner of Match 1', player2: 'Winner of Match 2' },
                  { id: 6, player1: 'Winner of Match 3', player2: 'Winner of Match 4' }
                ]
              },
              {
                round: 'final',
                matches: [
                  { id: 7, player1: 'Winner of Match 5', player2: 'Winner of Match 6' }
                ]
              }
            ],
            tournament_id: 'tournament_456'
          };
          break;
      }
      
      // For demo purposes, tools always succeed
      const toolStatus: ToolCall['status'] = 'running';
      
      const botResponse: Message = {
        id: Date.now() + 1,
        sender: 'agent',
        text: initialResponseText,
        timestamp: new Date(),
        toolCalls: [
          {
            tool: selectedTool,
            input: toolInput,
            status: toolStatus,
          }
        ]
      };
      
      setMessages(prev => [...prev, botResponse]);
      scrollToBottom(); // Restore this call
      
      // Tools always run and complete successfully
      const toolCompletionTime = 1500 + Math.random() * 2000;
      
      setTimeout(() => {
        setMessages(prev => {
          const updatedMessages = [...prev];
          const lastMessage = updatedMessages.find(m => m.id === botResponse.id);
          
          if (lastMessage?.toolCalls && lastMessage.toolCalls.length > 0) {
            const toolCall = lastMessage.toolCalls[0];
            toolCall.status = 'complete';
            toolCall.output = mockOutput;
            
            // Use imported response templates
            const template = responseTemplates[toolCall.tool] || responseTemplates.set_reminder;
            
            // Store the initial response (first paragraph)
            const initialResponse = lastMessage.text.split('\n\n')[0];
            
            // Construct the new message
            lastMessage.text = initialResponse;
            lastMessage.text += `\n\n${template.header}`;
            lastMessage.text += `\n\n${template.intro}`;
            lastMessage.text += template.content;
            lastMessage.text += template.conclusion;
          }
          
          return updatedMessages.map(m => m.id === botResponse.id && lastMessage ? lastMessage : m);
        });
        
        scrollToBottom(); // Restore this call
      }, toolCompletionTime);
      
    }, processingTime);
  }, [inputValue, onSendMessage]);

  // Handle textarea height adjustment
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    
    // Adjust height dynamically
    const currentScrollHeight = e.target.scrollHeight;
    // Set a max height (e.g., 200px)
    const maxHeight = 200;
    setTextareaHeight(Math.min(currentScrollHeight, maxHeight) + 'px');
  };

  // Handle Enter key to send message
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTimestamp = (date: Date) => {
    const today = new Date();
    const isToday = date.getDate() === today.getDate() &&
                    date.getMonth() === today.getMonth() &&
                    date.getFullYear() === today.getFullYear();
    
    if (isToday) {
      return `Today at ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    } else {
      // Check if yesterday
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      const isYesterday = date.getDate() === yesterday.getDate() &&
                          date.getMonth() === yesterday.getMonth() &&
                          date.getFullYear() === yesterday.getFullYear();
      
      if (isYesterday) {
        return `Yesterday at ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`;
      } else {
        return date.toLocaleDateString([], { month: '2-digit', day: '2-digit', year: 'numeric' }) + ` ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`;
      }
    }
  };

  return (
    <div className="flex h-[720px] max-w-6xl mx-auto rounded-lg overflow-hidden border border-gray-200 shadow-lg">
      {/* Server sidebar */}
      <div className="w-[72px] bg-[#202225] flex flex-col items-center py-3 space-y-2">
        {servers.map(server => (
          <div 
            key={server.id}
            className={`relative group cursor-pointer ${server.active ? 'rounded-2xl' : 'rounded-full hover:rounded-2xl'} w-12 h-12 flex items-center justify-center transition-all duration-200`}
          >
            {server.hasNotification && (
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">•</span>
              </div>
            )}
            <div 
              className={`absolute left-0 w-1 ${server.active ? 'h-10' : 'h-0 group-hover:h-5'} bg-white rounded-r-full transition-all duration-200`}
            />
            <div className={`w-12 h-12 ${server.active ? 'bg-[#5865F2]' : 'bg-[#36393f] hover:bg-[#5865F2]'} rounded-full flex items-center justify-center text-white font-medium`}>
              {server.name.substring(0, 2)}
            </div>
          </div>
        ))}
      </div>
      
      {/* Channel sidebar */}
      <div className="w-60 bg-[#2f3136] text-[#dcddde] flex flex-col">
        <div className="p-4 border-b border-[#202225] shadow-sm">
          <h2 className="font-bold text-white truncate">AgentSpot</h2>
        </div>
        
        <div className="overflow-y-auto flex-grow">
          <div className="px-2 py-4">
            {/* Text channels */}
            <div className="mb-2">
              <div className="px-2 mb-1 flex items-center justify-between">
                <h3 className="text-xs uppercase font-bold text-[#8e9297] tracking-wide">Text Channels</h3>
                <button className="text-[#8e9297] hover:text-white">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              <ul>
                {channels.text.map(channel => (
                  <li 
                    key={channel.id} 
                    className={`flex items-center px-2 py-1 rounded text-sm cursor-pointer ${channel.selected ? 'bg-[#393c43] text-white' : channel.unread ? 'font-semibold' : 'text-[#8e9297] hover:text-[#dcddde] hover:bg-[#34373c]'}`}
                  >
                    <span className="mr-1 text-[#8e9297]">#</span>
                    <span>{channel.name}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Voice channels */}
            <div className="mb-2">
              <div className="px-2 mb-1 flex items-center justify-between">
                <h3 className="text-xs uppercase font-bold text-[#8e9297] tracking-wide">Voice Channels</h3>
              </div>
              <ul>
                {channels.voice.map(channel => (
                  <li 
                    key={channel.id} 
                    className="flex items-center px-2 py-1 rounded text-sm text-[#8e9297] hover:text-[#dcddde] hover:bg-[#34373c] cursor-pointer"
                  >
                    <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                    </svg>
                    <span>{channel.name}</span>
                    {channel.users > 0 && (
                      <span className="ml-auto text-xs">{channel.users}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Members */}
            <div className="mt-4">
              <h3 className="px-2 mb-1 text-xs uppercase font-bold text-[#8e9297] tracking-wide">Members — {members.length}</h3>
              <ul>
                {members.map(member => (
                  <li 
                    key={member.id} 
                    className="flex items-center px-2 py-1 rounded text-sm text-[#8e9297] hover:text-[#dcddde] hover:bg-[#34373c] cursor-pointer"
                  >
                    <div className="relative mr-2">
                      <div className="w-8 h-8 rounded-full bg-[#36393f] flex items-center justify-center text-white font-medium">
                        {member.name.substring(0, 1)}
                      </div>
                      <div className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-[#2f3136] ${member.status === 'online' ? 'bg-green-500' : member.status === 'idle' ? 'bg-yellow-500' : member.status === 'dnd' ? 'bg-red-500' : 'bg-gray-500'}`}></div>
                    </div>
                    <span className={`${member.role === 'Admin' ? 'text-[#fa9c21]' : ''}`}>
                      {member.name}
                    </span>
                    {member.isBot && (
                      <span className="ml-2 text-xs bg-[#5865F2] text-white px-1 rounded text-[10px]">BOT</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        
        {/* User area */}
        <div className="p-2 bg-[#292b2f] flex items-center mt-auto">
          <div className="relative mr-2">
            <div className="w-8 h-8 rounded-full bg-[#5865F2] flex items-center justify-center text-white font-medium">
              U
            </div>
            <div className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-[#292b2f] bg-green-500"></div>
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-white">Username</div>
            <div className="text-xs text-[#b9bbbe]">#1234</div>
          </div>
          <div className="flex space-x-1">
            <button className="text-[#b9bbbe] hover:text-white p-1">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" />
              </svg>
            </button>
            <button className="text-[#b9bbbe] hover:text-white p-1">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 4a1 1 0 011-1h8a1 1 0 011 1v12a1 1 0 01-1 1H6a1 1 0 01-1-1V4zm4 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" />
              </svg>
            </button>
            <button className="text-[#b9bbbe] hover:text-white p-1">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      {/* Main chat area */}
      <div className="flex-1 flex flex-col bg-[#36393f]">
        {/* Channel header */}
        <div className="border-b border-[#202225] px-4 py-3 flex items-center bg-[#36393f]">
          <div className="mr-2 text-[#8e9297]">#</div>
          <div className="flex-1">
            <h2 className="font-bold text-white">bot-commands</h2>
          </div>
          <div className="flex items-center space-x-3">
            <button className="text-[#b9bbbe] hover:text-white">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
            </button>
            <button className="text-[#b9bbbe] hover:text-white">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </button>
            <button className="text-[#b9bbbe] hover:text-white">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </button>
            <button className="text-[#b9bbbe] hover:text-white">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M5 3a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V5a2 2 0 00-2-2H5zm0 2h10v7h-2l-1 2H8l-1-2H5V5z" clipRule="evenodd" />
              </svg>
            </button>
            <button className="text-[#b9bbbe] hover:text-white">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Messages area */}
        <div className="flex-1 overflow-y-auto p-4 text-[#dcddde] relative">
          <AnimatePresence initial={false}>
            {messages.map((message, index) => {
              // Check if the previous message is from the same sender and close in time
              const prevMessage = messages[index - 1];
              const isContinuation = 
                prevMessage && 
                prevMessage.sender === message.sender && 
                message.timestamp.getTime() - prevMessage.timestamp.getTime() < 5 * 60 * 1000; // 5 minutes threshold
              
              return (
                <motion.div 
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`flex group hover:bg-[#32353b] -mx-4 px-4 ${isContinuation ? 'mt-0.5' : 'mt-4'}`}
                >
                  {!isContinuation ? (
                    <div className="w-10 h-10 rounded-full mr-4 flex-shrink-0 mt-1">
                      {message.sender === 'agent' ? (
                        <div className="w-full h-full bg-[#5865F2] rounded-full flex items-center justify-center text-white font-bold relative">
                          <Bot size={24} />
                          <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-[#36393f] bg-green-500"></div>
                        </div>
                      ) : (
                        <div className="w-full h-full bg-gray-700 rounded-full flex items-center justify-center text-white font-bold relative">
                          U
                          <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-[#36393f] bg-green-500"></div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="w-10 mr-4 flex-shrink-0 relative">
                      {/* Show timestamp on hover for continued messages */}
                      <span className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-full pr-2 text-[10px] text-[#72767d] opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {message.timestamp.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    {!isContinuation && (
                      <div className="flex items-baseline mb-0.5">
                        <span className={`font-medium mr-2 ${message.sender === 'agent' ? 'text-[#5865F2]' : 'text-white'}`}>
                          {message.sender === 'agent' ? 'AgentBot' : 'Username'}
                          {message.sender === 'agent' && <span className="ml-1.5 text-xs bg-[#5865F2] text-white px-1 py-0.5 rounded">BOT</span>}
                        </span>
                        <span className="text-xs text-[#72767d] font-medium">
                          {formatTimestamp(message.timestamp)}
                        </span>
                      </div>
                    )}
                    
                    {/* Split message display into initial response and tool results */}
                    {message.sender === 'agent' && message.toolCalls && message.toolCalls.length > 0 ? (
                      <>
                        {/* Initial response */}
                        <div className={`whitespace-pre-wrap break-words text-[#dcddde]`}>
                          <ReactMarkdown
                            components={{
                              code: ({node, inline, className, children, ...props}: any) => {
                                /language-(\w+)/.exec(className || '');
                                return !inline ? (
                                  <pre className="bg-[#2f3136] rounded p-2 my-2 whitespace-pre-wrap break-words text-sm">
                                    <code className={className} {...props}>
                                      {children}
                                    </code>
                                  </pre>
                                ) : (
                                  <code className="bg-[#2f3136] text-xs px-1 py-0.5 rounded" {...props}>
                                    {children}
                                  </code>
                                );
                              },
                              strong: ({node, children, ...props}: any) => {
                                return (
                                  <strong className="font-bold text-white" {...props}>
                                    {children}
                                  </strong>
                                );
                              },
                              a: ({node, children, ...props}: any) => {
                                return (
                                  <a className="text-[#00aff4] hover:underline" {...props}>
                                    {children}
                                  </a>
                                );
                              },
                              p: ({node, children, ...props}: any) => {
                                return (
                                  <p className="mt-1 mb-2" {...props}>
                                    {children}
                                  </p>
                                );
                              }
                            }}
                          >
                            {/* Display only the initial response */}
                            {message.text ? message.text.split('\n\n')[0] : 'Processing...'}
                          </ReactMarkdown>
                        </div>
                        
                        {/* Tool calls - display immediately after initial response */}
                        {message.toolCalls.map((toolCall, index) => (
                          <div key={index} className="mt-2 mb-2">
                            {/* Tool call as message */}
                            <div className="flex items-start">
                              <div className="text-lg mr-2">
                                {toolCall.tool === 'search_game_news' && '🎮'}
                                {toolCall.tool === 'play_music' && '🎵'}
                                {toolCall.tool === 'generate_price_chart' && '📈'}
                                {toolCall.tool === 'create_poll' && '📊'}
                                {toolCall.tool === 'set_reminder' && '⏰'}
                                {toolCall.tool === 'retrieve_campaign_status' && '🎲'}
                                {toolCall.tool === 'generate_tournament_brackets' && '🏆'}
                                {!['search_game_news', 'play_music', 'generate_price_chart', 'create_poll', 'set_reminder', 'retrieve_campaign_status', 'generate_tournament_brackets'].includes(toolCall.tool) && '🤖'}
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-white">
                                  {toolCall.tool === 'search_game_news' && 'GameNews Bot'}
                                  {toolCall.tool === 'play_music' && 'Discord Music'}
                                  {toolCall.tool === 'generate_price_chart' && 'Financial Charts'}
                                  {toolCall.tool === 'create_poll' && 'Poll Master'}
                                  {toolCall.tool === 'set_reminder' && 'Reminder Bot'}
                                  {toolCall.tool === 'retrieve_campaign_status' && 'D&D Campaign'}
                                  {toolCall.tool === 'generate_tournament_brackets' && 'Tournament Brackets'}
                                  {!['search_game_news', 'play_music', 'generate_price_chart', 'create_poll', 'set_reminder', 'retrieve_campaign_status', 'generate_tournament_brackets'].includes(toolCall.tool) && toolCall.tool}
                                  <span className="ml-1.5 text-xs bg-[#5865F2] text-white px-1 py-0.5 rounded">BOT</span>
                                  <span className="ml-1.5 text-xs text-[#72767d] font-normal">
                                    {new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}
                                    {' '}• {toolCall.status === 'running' ? '⚙️ processing...' : '✅ complete'}
                                  </span>
                                </div>
                                
                                {/* Input as Discord-styled message */}
                                {toolCall.input && (
                                  <div className="mt-1 text-[#dcddde]">
                                    <div className="flex items-center">
                                      <span className="mr-1">
                                        {toolCall.tool === 'search_game_news' && '🔍'}
                                        {toolCall.tool === 'play_music' && '🎧'}
                                        {toolCall.tool === 'generate_price_chart' && '💹'}
                                        {toolCall.tool === 'create_poll' && '❓'}
                                        {toolCall.tool === 'set_reminder' && '📝'}
                                        {toolCall.tool === 'retrieve_campaign_status' && '📜'}
                                        {toolCall.tool === 'generate_tournament_brackets' && '🏆'}
                                        {!['search_game_news', 'play_music', 'generate_price_chart', 'create_poll', 'set_reminder', 'retrieve_campaign_status', 'generate_tournament_brackets'].includes(toolCall.tool) && '🔄'}
                                      </span>
                                      <span className="bg-[#2f3136] text-sm px-2 py-1 rounded-md">
                                        {typeof toolCall.input === 'object' ? (
                                          <>
                                            {toolCall.tool === 'search_game_news' && toolCall.input.query && (
                                              <span>Searching for <span className="text-white font-medium">{toolCall.input.query}</span> 🔎</span>
                                            )}
                                            {toolCall.tool === 'play_music' && toolCall.input.query && (
                                              <span>Playing <span className="text-white font-medium">{toolCall.input.query}</span> 🎧</span>
                                            )}
                                            {toolCall.tool === 'generate_price_chart' && toolCall.input.assets && (
                                              <span>Creating price chart for <span className="text-white font-medium">{toolCall.input.assets.join(' vs ')}</span> over {toolCall.input.timeframe} 📊</span>
                                            )}
                                            {toolCall.tool === 'create_poll' && toolCall.input.question && (
                                              <span>Creating poll: <span className="text-white font-medium">{toolCall.input.question}</span> 🗳️</span>
                                            )}
                                            {toolCall.tool === 'set_reminder' && toolCall.input.reminder_text && (
                                              <span>Setting reminder: <span className="text-white font-medium">{toolCall.input.reminder_text}</span> for {toolCall.input.time_offset} ⏱️</span>
                                            )}
                                            {toolCall.tool === 'retrieve_campaign_status' && toolCall.input.campaign_id && (
                                              <span>Retrieving campaign status for <span className="text-white font-medium">{toolCall.input.campaign_id}</span> 📜</span>
                                            )}
                                            {toolCall.tool === 'generate_tournament_brackets' && toolCall.input.tournament_id && (
                                              <span>Generating brackets for <span className="text-white font-medium">{toolCall.input.tournament_id}</span> 🏆</span>
                                            )}
                                          </>
                                        ) : (
                                          String(toolCall.input)
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Output as Discord embed */}
                                {toolCall.output && (
                                  <div className="mt-2 border-l-4 border-[#5865F2] bg-[#2f3136] rounded-md p-3 max-w-3xl text-[#dcddde]">
                                    {toolCall.tool === 'search_game_news' && toolCall.output.results && (
                                      <>
                                        <div className="font-medium text-white mb-2 flex items-center">
                                          <span className="mr-2">🎮</span>
                                          League of Legends Patch Updates
                                        </div>
                                        {toolCall.output.results.map((result: any, i: number) => (
                                          <div key={i} className="mb-3 ml-2 border-b border-[#40444b] pb-3 last:border-0 last:pb-0">
                                            <div className="text-white font-medium flex items-center">
                                              <span className="mr-2">{i === 0 ? '📝' : '📢'}</span>
                                              {result.title}
                                            </div>
                                            <div className="flex items-center mt-1 text-xs mb-2">
                                              <span className="mr-2">🔗 {result.source}</span>
                                              <a href={result.url} className="text-[#00aff4] hover:underline">View details</a>
                                            </div>
                                            {result.highlights && result.highlights.length > 0 && (
                                              <div className="bg-[#202225] rounded p-2 mt-1 text-sm">
                                                <div className="mb-1">Key changes:</div>
                                                <ul className="list-disc list-inside space-y-1 ml-1">
                                                  {result.highlights.map((highlight: string, j: number) => (
                                                    <li key={j} className="opacity-90">{highlight}</li>
                                                  ))}
                                                </ul>
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </>
                                    )}
                                    {toolCall.tool === 'play_music' && toolCall.output.track && (
                                      <>
                                        <div className="font-medium text-white mb-2 flex items-center">
                                          <span className="mr-2">🎵</span>
                                          Now Playing
                                        </div>
                                        <div className="flex items-center bg-[#202225] p-2 rounded-md">
                                          <div className="w-12 h-12 bg-[#36393f] rounded-md flex items-center justify-center mr-3">
                                            <span className="text-2xl">🎧</span>
                                          </div>
                                          <div>
                                            <div className="text-white font-medium">{toolCall.output.track}</div>
                                            <div className="text-sm">👤 {toolCall.output.artist} • 🎮 Requested by {toolCall.output.requester}</div>
                                          </div>
                                        </div>
                                      </>
                                    )}
                                    {toolCall.tool === 'generate_price_chart' && toolCall.output.chart_image_url && (
                                      <>
                                        <div className="font-medium text-white mb-2 flex items-center">
                                          <span className="mr-2">📈</span>
                                          Price Comparison Chart
                                        </div>
                                        <div className="bg-[#202225] p-3 rounded-md">
                                          <div className="mb-2 text-white font-medium">{toolCall.output.chart_title}</div>
                                          
                                          {/* Chart Image */}
                                          <div className="mb-3 bg-[#36393f] p-1 rounded">
                                            <img 
                                              src={toolCall.output.chart_image_url} 
                                              alt={toolCall.output.chart_title}
                                              className="w-full h-auto rounded max-h-[300px] object-contain"
                                            />
                                          </div>
                                          
                                          {/* Performance Summary */}
                                          <div className="mb-3">
                                            <div className="flex items-center text-white font-medium mb-1">
                                              <span className="mr-2">📊</span>
                                              Performance Summary
                                            </div>
                                            <div className="ml-6 space-y-2">
                                              {toolCall.output.assets.map((asset: any, i: number) => (
                                                <div key={i} className="flex items-center">
                                                  <div className={`w-3 h-3 rounded-full mr-2 ${i === 0 ? 'bg-[#5865F2]' : 'bg-[#2D9D78]'}`}></div>
                                                  <div className="text-sm flex-1">
                                                    <span className="text-white font-medium">{asset.name} ({asset.symbol}): </span>
                                                    <span className="font-mono">${asset.current_price.toLocaleString()}</span>
                                                    <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-[#36393f]">
                                                      ↑ {asset.percent_change_2y}% (2Y)
                                                    </span>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                          
                                          {/* Analysis */}
                                          <div>
                                            <div className="flex items-center text-white font-medium mb-1">
                                              <span className="mr-2">🔍</span>
                                              Analysis
                                            </div>
                                            <div className="ml-6 text-sm bg-[#36393f] p-2 rounded">
                                              {toolCall.output.analysis}
                                            </div>
                                          </div>
                                          
                                          {/* Timestamp */}
                                          <div className="text-xs mt-3 text-[#a3a6aa]">
                                            Data as of {new Date(toolCall.output.timestamp).toLocaleString()}
                                          </div>
                                        </div>
                                      </>
                                    )}
                                    {toolCall.tool === 'create_poll' && toolCall.output.question && (
                                      <>
                                        <div className="font-medium text-white mb-2 flex items-center">
                                          <span className="mr-2">📊</span>
                                          Poll
                                        </div>
                                        <div className="bg-[#202225] p-3 rounded-md">
                                          <div className="text-white font-medium mb-2">{toolCall.output.question}</div>
                                          {toolCall.output.options && toolCall.output.options.map((option: string, i: number) => (
                                            <div key={i} className="flex items-center mb-2 last:mb-0">
                                              <span className="mr-2">{['🇦', '🇧', '🇨', '🇩', '🇪'][i] || `${i+1}️⃣`}</span>
                                              <div className="flex-1">
                                                <div className="h-8 flex items-center">
                                                  <div className="bg-[#5865F2] h-6 rounded-md w-0 transition-all duration-300"></div>
                                                  <span className="ml-2">{option}</span>
                                                </div>
                                              </div>
                                              <span className="ml-2 text-xs">0%</span>
                                            </div>
                                          ))}
                                          <div className="text-xs mt-2">🗳️ Total votes: 0 • Poll ID: {toolCall.output.poll_id}</div>
                                        </div>
                                      </>
                                    )}
                                    {toolCall.tool === 'set_reminder' && toolCall.output.reminder_text && (
                                      <>
                                        <div className="font-medium text-white mb-2 flex items-center">
                                          <span className="mr-2">⏰</span>
                                          Reminder Set
                                        </div>
                                        <div className="bg-[#202225] p-2 rounded-md">
                                          <div className="mb-1 flex items-start">
                                            <span className="mr-2">📝</span>
                                            <span className="text-white">{toolCall.output.reminder_text}</span>
                                          </div>
                                          <div className="mb-1 flex items-start">
                                            <span className="mr-2">⏱️</span>
                                            <span>Reminds at {new Date(toolCall.output.remind_at).toLocaleString()}</span>
                                          </div>
                                          <div className="text-xs mt-1">🆔 Reminder ID: {toolCall.output.reminder_id}</div>
                                        </div>
                                      </>
                                    )}
                                    {toolCall.tool === 'retrieve_campaign_status' && toolCall.output.campaign_name && (
                                      <>
                                        <div className="font-medium text-white mb-2 flex items-center">
                                          <span className="mr-2">🎲</span>
                                          D&D Campaign Status
                                        </div>
                                        <div className="bg-[#202225] p-3 rounded-md">
                                          <div className="mb-2">
                                            <div className="flex items-center text-white font-medium mb-1">
                                              <span className="mr-2">📜</span>
                                              {toolCall.output.campaign_name}
                                            </div>
                                            <div className="text-sm ml-6">Session #{toolCall.output.session_number} • {new Date(toolCall.output.session_date).toLocaleDateString()}</div>
                                          </div>
                                          
                                          <div className="mb-3">
                                            <div className="flex items-center text-white font-medium mb-1">
                                              <span className="mr-2">🗺️</span>
                                              Current Location
                                            </div>
                                            <div className="ml-6 bg-[#36393f] p-2 rounded">
                                              {toolCall.output.party_location}
                                            </div>
                                          </div>
                                          
                                          <div className="mb-3">
                                            <div className="flex items-center text-white font-medium mb-1">
                                              <span className="mr-2">👥</span>
                                              Party Status
                                            </div>
                                            <div className="ml-6">
                                              {toolCall.output.party_members.map((member: any, i: number) => (
                                                <div key={i} className="flex items-center mb-1 last:mb-0">
                                                  <div className={`w-2 h-2 rounded-full mr-2 ${
                                                    member.status === 'Healthy' ? 'bg-green-500' : 
                                                    member.status === 'Low health' ? 'bg-yellow-500' :
                                                    member.status === 'Cursed' ? 'bg-purple-500' : 'bg-red-500'
                                                  }`}></div>
                                                  <div className="text-white font-medium mr-2">{member.name}</div>
                                                  <div className="text-sm opacity-80">Lvl {member.level} {member.class} • {member.status}</div>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                          
                                          <div className="mb-3">
                                            <div className="flex items-center text-white font-medium mb-1">
                                              <span className="mr-2">⚔️</span>
                                              Current Quest
                                            </div>
                                            <div className="ml-6 bg-[#36393f] p-2 rounded text-sm">
                                              {toolCall.output.quest_status}
                                            </div>
                                          </div>
                                          
                                          <div>
                                            <div className="flex items-center text-white font-medium mb-1">
                                              <span className="mr-2">📝</span>
                                              DM Notes
                                            </div>
                                            <div className="ml-6 text-sm italic opacity-80">
                                              {toolCall.output.dm_notes}
                                            </div>
                                          </div>
                                        </div>
                                      </>
                                    )}
                                    {toolCall.tool === 'generate_tournament_brackets' && toolCall.output.tournament_name && (
                                      <>
                                        <div className="font-medium text-white mb-2 flex items-center">
                                          <span className="mr-2">🏆</span>
                                          Tournament Brackets
                                        </div>
                                        <div className="bg-[#202225] p-3 rounded-md">
                                          <div className="mb-2">
                                            <div className="text-white font-medium">{toolCall.output.tournament_name}</div>
                                            <div className="text-sm opacity-80 mt-1">
                                              <span className="mr-2">🎮 {toolCall.output.game}</span>
                                              <span className="mr-2">• 📋 {toolCall.output.format}</span>
                                              <span>• ⏰ {new Date(toolCall.output.start_time).toLocaleTimeString()}</span>
                                            </div>
                                          </div>
                                          
                                          <div className="mt-3 mb-2">
                                            <div className="flex items-center text-white font-medium mb-1">
                                              <span className="mr-2">👥</span>
                                              Participants ({toolCall.output.participants.length})
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 ml-6">
                                              {toolCall.output.participants.map((participant: any, i: number) => (
                                                <div key={i} className="flex items-center">
                                                  <div className={`w-2 h-2 rounded-full mr-2 ${
                                                    participant.status === 'online' ? 'bg-green-500' : 
                                                    participant.status === 'idle' ? 'bg-yellow-500' : 'bg-gray-500'
                                                  }`}></div>
                                                  <div className="text-sm">
                                                    <span className="opacity-70 mr-1">#{participant.seed}</span>
                                                    <span className="text-white">{participant.name}</span>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                          
                                          <div className="mt-3">
                                            <div className="flex items-center text-white font-medium mb-2">
                                              <span className="mr-2">🔀</span>
                                              Bracket Matchups
                                            </div>
                                            
                                            {toolCall.output.brackets[0] && (
                                              <div className="mb-3">
                                                <div className="text-sm font-medium mb-1 ml-6">Round 1</div>
                                                <div className="space-y-2 ml-6">
                                                  {toolCall.output.brackets[0].matches.map((match: any, i: number) => (
                                                    <div key={i} className="bg-[#36393f] p-2 rounded flex items-center">
                                                      <div className="w-5 opacity-70 mr-2">#{match.id}</div>
                                                      <div className="flex-1 flex justify-between">
                                                        <div className="font-medium text-white">{match.player1}</div>
                                                        <div className="text-xs opacity-70">vs</div>
                                                        <div className="font-medium text-white">{match.player2}</div>
                                                      </div>
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                            
                                            {toolCall.output.brackets[1] && (
                                              <div className="mb-3">
                                                <div className="text-sm font-medium mb-1 ml-6">Round 2</div>
                                                <div className="space-y-2 ml-6">
                                                  {toolCall.output.brackets[1].matches.map((match: any, i: number) => (
                                                    <div key={i} className="bg-[#36393f] p-2 rounded flex items-center opacity-50">
                                                      <div className="w-5 opacity-70 mr-2">#{match.id}</div>
                                                      <div className="flex-1 flex justify-between">
                                                        <div className="font-medium text-white">{match.player1}</div>
                                                        <div className="text-xs opacity-70">vs</div>
                                                        <div className="font-medium text-white">{match.player2}</div>
                                                      </div>
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                            
                                            {toolCall.output.brackets[2] && (
                                              <div>
                                                <div className="text-sm font-medium mb-1 ml-6">Final</div>
                                                <div className="space-y-2 ml-6">
                                                  {toolCall.output.brackets[2].matches.map((match: any, i: number) => (
                                                    <div key={i} className="bg-[#36393f] p-2 rounded flex items-center opacity-50">
                                                      <div className="w-5 opacity-70 mr-2">#{match.id}</div>
                                                      <div className="flex-1 flex justify-between">
                                                        <div className="font-medium text-white">{match.player1}</div>
                                                        <div className="text-xs opacity-70">vs</div>
                                                        <div className="font-medium text-white">{match.player2}</div>
                                                      </div>
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                          
                                          <div className="text-xs mt-3 opacity-70">Tournament ID: {toolCall.output.tournament_id}</div>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      // Regular message display (non-tool messages)
                      <div className={`whitespace-pre-wrap break-words text-[#dcddde]`}>
                        <ReactMarkdown
                          components={{
                            code: ({node, inline, className, children, ...props}: any) => {
                              /language-(\w+)/.exec(className || '');
                              return !inline ? (
                                <pre className="bg-[#2f3136] rounded p-2 my-2 whitespace-pre-wrap break-words text-sm">
                                  <code className={className} {...props}>
                                    {children}
                                  </code>
                                </pre>
                              ) : (
                                <code className="bg-[#2f3136] text-xs px-1 py-0.5 rounded" {...props}>
                                  {children}
                                </code>
                              );
                            },
                            strong: ({node, children, ...props}: any) => {
                              return (
                                <strong className="font-bold text-white" {...props}>
                                  {children}
                                </strong>
                              );
                            },
                            a: ({node, children, ...props}: any) => {
                              return (
                                <a className="text-[#00aff4] hover:underline" {...props}>
                                  {children}
                                </a>
                              );
                            },
                            p: ({node, children, ...props}: any) => {
                              return (
                                <p className="mt-1 mb-2" {...props}>
                                  {children}
                                </p>
                              );
                            }
                          }}
                        >
                          {message.text}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
          
          {/* Typing indicators */}
          <AnimatePresence>
            {isTyping && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex items-center text-[#b9bbbe] text-sm mt-2 mb-4 pl-14" // Added mb-4 to give space below typing indicator
              >
                <div className="flex items-center">
                  <span className="mr-1 font-medium">AgentBot</span>
                  <span className="text-xs font-medium mr-2">is typing</span>
                  <div className="flex space-x-1">
                    <span className="animate-bounce w-1.5 h-1.5 bg-[#b9bbbe] rounded-full delay-0"></span>
                    <span className="animate-bounce w-1.5 h-1.5 bg-[#b9bbbe] rounded-full delay-150"></span>
                    <span className="animate-bounce w-1.5 h-1.5 bg-[#b9bbbe] rounded-full delay-300"></span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          <div ref={messagesEndRef} className="h-4" /> {/* Increased height to add extra space at the bottom */}
        </div>
        
        {/* Input area */}
        <div className="px-4 pb-4 pt-2 mt-auto flex-shrink-0"> {/* Added some padding at the top */}
          <div className="relative bg-[#40444b] rounded-lg flex items-center">
            <button className="p-3 text-[#b9bbbe] hover:text-white">
              <Plus size={20} />
            </button>
            <textarea
              ref={inputRef}
              className="flex-1 bg-transparent border-none resize-none py-2.5 text-[#dcddde] placeholder-[#72767d] outline-none text-sm max-h-[200px] overflow-y-auto" // Added max-h & overflow
              placeholder={`Message #${channels.text.find(c => c.selected)?.name || 'bot-commands'}`} // Dynamic placeholder
              value={inputValue}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              rows={1}
              style={{ height: textareaHeight }} // Apply dynamic height
              disabled={demoInProgress} // Prevent interaction during auto demo typing
            />
            <div className="flex items-center space-x-1 p-2">
              <button className="text-[#b9bbbe] hover:text-white p-1.5">
                <Gift size={22} />
              </button>
              <button className="text-[#b9bbbe] hover:text-white p-1.5">
                {/* Placeholder for GIF/Sticker */}
                <svg className="w-[22px] h-[22px]" viewBox="0 0 24 24" fill="currentColor"><path d="M4.00781 3C3.45181 3 3 3.45181 3 4.00781V19.9922C3 20.5482 3.45181 21 4.00781 21H19.9922C20.5482 21 21 20.5482 21 19.9922V4.00781C21 3.45181 20.5482 3 19.9922 3H4.00781ZM5 5H19V19H5V5ZM7 7V11H11V7H7ZM13 7V9H17V7H13ZM7 13V17H11V13H7ZM13 11V13H17V11H13ZM13 15V17H17V15H13Z"></path></svg>
              </button>
              <button className="text-[#b9bbbe] hover:text-white p-1.5">
                <Smile size={22} />
              </button>
              {/* Send Button - appears when typing */}
              {inputValue.trim() && (
                <button 
                  onClick={handleSendMessage}
                  className="text-[#5865F2] hover:text-[#4752c4] p-1.5"
                  disabled={demoInProgress} // Prevent interaction during auto demo typing
                >
                  <Send size={22} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Members sidebar */}
      <div className="w-60 bg-[#2f3136] text-[#8e9297] flex-shrink-0 border-l border-[#202225] overflow-y-auto p-4 hidden xl:block">
        {/* Online members */}
        <h3 className="text-xs uppercase font-bold tracking-wide mb-2">Online — {members.filter(m => m.status === 'online').length}</h3>
        <ul>
          {members.filter(m => m.status === 'online').map(member => (
            <li key={member.id} className="flex items-center px-2 py-1.5 rounded hover:bg-[#393c43] hover:text-[#dcddde] cursor-pointer group mb-1">
              <div className="relative mr-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-medium ${member.isBot ? 'bg-[#5865F2]' : 'bg-gray-700'}`}>
                  {member.isBot ? <Bot size={18}/> : member.name.substring(0, 1)}
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-[#2f3136] bg-green-500"></div>
              </div>
              <div className="flex-1 truncate">
                <span className={`${member.role === 'Admin' ? 'text-[#fa9c21]' : member.isBot ? 'text-[#5865F2]' : 'text-[#dcddde]'}`}>{member.name}</span>
                {member.isBot && (
                  <span className="ml-1.5 text-[10px] bg-[#5865F2] text-white px-1 py-0.5 rounded align-middle">BOT</span>
                )}
              </div>
            </li>
          ))}
        </ul>

        {/* Idle members */}
        <h3 className="text-xs uppercase font-bold tracking-wide mt-4 mb-2">Idle — {members.filter(m => m.status === 'idle').length}</h3>
        <ul>
          {members.filter(m => m.status === 'idle').map(member => (
            <li key={member.id} className="flex items-center px-2 py-1.5 rounded hover:bg-[#393c43] hover:text-[#dcddde] cursor-pointer group mb-1 opacity-80">
              <div className="relative mr-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium bg-gray-700">
                  {member.name.substring(0, 1)}
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-[#2f3136] bg-yellow-500 flex items-center justify-center">
                  <svg className="w-2 h-2 text-[#2f3136]" fill="currentColor" viewBox="0 0 12 12"><path d="M6 10C8.20914 10 10 8.20914 10 6C10 3.79086 8.20914 2 6 2C3.79086 2 2 3.79086 2 6C2 8.20914 3.79086 10 6 10Z"></path></svg>
                </div>
              </div>
              <div className="flex-1 truncate">
                <span className={`${member.role ? 'text-[#fa9c21]' : 'text-[#dcddde]'}`}>{member.name}</span>
              </div>
            </li>
          ))}
        </ul>

        {/* Do Not Disturb members */}
        <h3 className="text-xs uppercase font-bold tracking-wide mt-4 mb-2">Do Not Disturb — {members.filter(m => m.status === 'dnd').length}</h3>
        <ul>
          {members.filter(m => m.status === 'dnd').map(member => (
            <li key={member.id} className="flex items-center px-2 py-1.5 rounded hover:bg-[#393c43] hover:text-[#dcddde] cursor-pointer group mb-1 opacity-80">
              <div className="relative mr-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium bg-gray-700">
                  {member.name.substring(0, 1)}
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-[#2f3136] bg-red-500 flex items-center justify-center">
                  <div className="w-1.5 h-0.5 bg-[#2f3136] rounded-sm"></div>
                </div>
              </div>
              <div className="flex-1 truncate">
                <span className={`${member.role ? 'text-[#fa9c21]' : 'text-[#dcddde]'}`}>{member.name}</span>
                {member.role === 'Moderator' && (
                  <span className="ml-1.5 text-[10px] bg-[#4f545c] text-[#b9bbbe] px-1 py-0.5 rounded align-middle">Mod</span>
                )}
              </div>
            </li>
          ))}
        </ul>

        {/* Offline members */}
        <h3 className="text-xs uppercase font-bold tracking-wide mt-4 mb-2">Offline — {members.filter(m => m.status === 'offline').length}</h3>
        <ul>
          {members.filter(m => m.status === 'offline').map(member => (
            <li key={member.id} className="flex items-center px-2 py-1.5 rounded hover:bg-[#393c43] hover:text-[#dcddde] cursor-pointer group mb-1 opacity-50">
              <div className="relative mr-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium bg-gray-700">
                  {member.name.substring(0, 1)}
                </div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-[#2f3136] bg-gray-500 flex items-center justify-center">
                  <div className="w-2 h-2 bg-[#2f3136] rounded-full"></div>
                </div>
              </div>
              <div className="flex-1 truncate">
                <span className={`${member.role ? 'text-[#fa9c21]' : 'text-[#dcddde]'}`}>{member.name}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default DiscordInterface;